import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ModalAlertOneButton, { ModalAlertIcon } from '../modal/ModalAlertOneButton';
import {
    ScheduleConfirmAttempt,
    useScheduleConfirmAttemptSlot,
} from './scheduleConfirmAttempt';

export type ScheduleConfirmDialogContent = {
    title: string;
    content: string;
    confirmText: string;
    cancelText: string;
    icon?: ModalAlertIcon;
};

export type OpenScheduleConfirm = (content: ScheduleConfirmDialogContent, onConfirm: () => void) => void;

type OpenDialog = ScheduleConfirmDialogContent & {
    attempt: ScheduleConfirmAttempt;
    onConfirm: () => void;
};

/**
 * 공통 알럿(App.tsx, z-index 10001)보다 한 층 위. 이 창은 늘 사용자가 보는 맨 위 창이어야 한다 —
 * 같은 층이면 App 이 뒤에 렌더한 공통 알럿이 이 창을 가리고, 가려진 이 창의 버튼이 키보드(Enter)로
 * 눌려 수업이 나갔다(교차검증 2단계 재현).
 *
 * 포털로 body 바로 아래에 붙여 화면 쪽 쌓임 맥락(transform·z-index 를 가진 조상)에 갇히지 않게 한다.
 */
export const SCHEDULE_CONFIRM_DIALOG_Z_INDEX = 10002;

const FOCUSABLE_SELECTOR =
    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** 창 밖의 다른 요소가 포커스를 가져갔는가. 포커스가 창 안이거나 아무 데도 없으면 false. */
export const isFocusTakenOutside = (container: HTMLElement | null): boolean => {
    if (!container) return true;
    const active = document.activeElement;
    if (!active || active === document.body || active === document.documentElement) return false;
    return !container.contains(active);
};

/**
 * 확인 창 맨 위 보장의 포커스 부분 — 열릴 때(그리고 창이 바뀔 때) 창 자체로 포커스를 옮기고,
 * Tab 순환과 창 밖으로 나간 포커스 되돌리기를 한다. 닫히면 앞 포커스로 돌려준다.
 * 날짜 확인 창(useScheduleConfirmDialog)과 수강권 정지 확인 창이 함께 쓴다(동작은 예전 그대로 옮긴 것).
 */
export const useConfirmDialogFocusGuard = (
    containerRef: React.MutableRefObject<HTMLElement | null>,
    isOpen: boolean,
    focusKey?: unknown,
) => {
    // 열릴 때(그리고 창이 바뀔 때) 창 자체로 포커스를 옮긴다. 닫히면 앞 포커스로 돌려준다.
    useEffect(() => {
        if (!isOpen) return undefined;
        const previous = document.activeElement as HTMLElement | null;
        containerRef.current?.focus();
        return () => {
            if (previous && typeof previous.focus === 'function' && document.contains(previous)) {
                previous.focus();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, focusKey]);

    // 포커스 가두기: Tab 순환 + 창 밖으로 나간 포커스 되돌리기.
    useEffect(() => {
        if (!isOpen) return undefined;
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Tab') return;
            const container = containerRef.current;
            if (!container) return;
            const focusables = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
            if (focusables.length === 0) {
                event.preventDefault();
                container.focus();
                return;
            }
            const first = focusables[0];
            const last = focusables[focusables.length - 1];
            const active = document.activeElement as HTMLElement | null;
            const inside = !!active && container.contains(active) && active !== container;
            if (event.shiftKey) {
                if (!inside || active === first) {
                    event.preventDefault();
                    last.focus();
                }
            } else if (!inside || active === last) {
                event.preventDefault();
                first.focus();
            }
        };
        const onFocusIn = (event: FocusEvent) => {
            const container = containerRef.current;
            if (container && event.target instanceof Node && !container.contains(event.target)) {
                container.focus();
            }
        };
        document.addEventListener('keydown', onKeyDown, true);
        document.addEventListener('focusin', onFocusIn, true);
        return () => {
            document.removeEventListener('keydown', onKeyDown, true);
            document.removeEventListener('focusin', onFocusIn, true);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);
};

/**
 * 수업 등록 직전 확인 창(날짜 경고·다 쓴 수강권)을 **이 화면 안에서** 띄운다.
 *
 * ⚠️ 공통 알럿(App.tsx createAlert)으로 띄우면 안 된다. 공통 알럿은 한 벌의 상태를 모든 화면이
 * 나눠 쓰고, 새 알럿이 콜백 없이 오면 **본문만 바꾸고 앞 알럿의 확인 콜백·버튼을 그대로 둔다.**
 * 확인 창이 떠 있는 동안 늦게 도착한 다른 알럿(예: 프로필 사진 변경 완료)이 뜨면, 그 창의
 * 버튼이 수업 전송을 실행했다(교차검증에서 실제 App 함수로 재현). 공통 동작은 로그인 화면 등이
 * 기대고 있어 바꾸지 않고, 이 확인 창만 공통 상태에서 떼어 낸다.
 *
 * 모양은 공통 알럿과 같은 컴포넌트(ModalAlertOneButton)라 기존 "다 쓴 수강권" 창과 똑같이 보인다.
 *
 * 맨 위 창 보장
 *  - 공통 알럿보다 위 층(SCHEDULE_CONFIRM_DIALOG_Z_INDEX)에 포털로 띄운다.
 *  - 열리면 포커스를 창 안으로 옮긴다. 버튼이 아니라 **창 자체**에 둬서 Enter 가 아무 버튼도
 *    누르지 않게 한다(Enter 한 번으로 수업이 나가지 않는다).
 *  - Tab·Shift+Tab 은 창 안 버튼 사이에서만 돈다. 포커스가 창 밖으로 나가면 다시 들인다.
 *  - 그래도 확인 버튼 실행 순간 **창 밖의 다른 요소가 포커스를 갖고 있으면**(무언가가 포커스를
 *    가져갔다 = 이 창이 맨 위라고 확신할 수 없다) 실행하지 않는다. 포커스가 아무 데도 없는 경우
 *    (body)는 통과시킨다 — 사파리는 버튼을 눌러도 포커스를 주지 않아 막으면 마우스 확인이 안 된다.
 *
 * 연타·취소 뒤·화면 이탈 뒤 콜백은 시도 표식이 막는다(scheduleConfirmAttempt).
 */
export const useScheduleConfirmDialog = (): {
    openConfirm: OpenScheduleConfirm;
    /** 차단 판정 등으로 열린 확인 창을 닫고 그 시도를 무효로 만든다. */
    closeConfirm: () => void;
    confirmDialog: React.ReactElement | null;
} => {
    const slot = useScheduleConfirmAttemptSlot();
    const [dialog, setDialog] = useState<OpenDialog | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);

    const openConfirm = useCallback<OpenScheduleConfirm>(
        (content, onConfirm) => {
            // 새 창은 앞 시도를 무효로 만든다(같은 화면에서 창은 하나뿐).
            setDialog({ ...content, attempt: slot.begin(), onConfirm });
        },
        [slot],
    );

    const closeConfirm = useCallback(() => {
        slot.invalidateAll();
        setDialog(null);
    }, [slot]);

    const isOpen = dialog !== null;
    const attemptId = dialog?.attempt.id;

    useConfirmDialogFocusGuard(containerRef, isOpen, attemptId);

    const confirmDialog = dialog
        ? createPortal(
              <div
                  ref={containerRef}
                  role="alertdialog"
                  aria-modal="true"
                  aria-label={dialog.title}
                  tabIndex={-1}
                  data-testid="schedule-confirm-dialog"
                  style={{
                      position: 'fixed',
                      inset: 0,
                      zIndex: SCHEDULE_CONFIRM_DIALOG_Z_INDEX,
                      outline: 'none',
                  }}
              >
                  <ModalAlertOneButton
                      title={dialog.title}
                      content={dialog.content}
                      isError={true}
                      icon={dialog.icon}
                      buttonContent={dialog.confirmText}
                      callBack={() => {
                          if (isFocusTakenOutside(containerRef.current)) return;
                          if (!slot.consume(dialog.attempt)) return;
                          dialog.onConfirm();
                      }}
                      isTwoButton={true}
                      twoButtonContent={dialog.cancelText}
                      twoButtonCallBack={() => slot.cancel(dialog.attempt)}
                      alertStateReset={() =>
                          setDialog((current) => (current?.attempt === dialog.attempt ? null : current))
                      }
                  />
              </div>,
              document.body,
          )
        : null;

    return { openConfirm, closeConfirm, confirmDialog };
};
